class FrameModeControl extends HTMLElement {
  #select;
  #storageKey;
  #sectionId;
  #trigger;
  #panel;
  #currentLabel;
  #currentSwatch;
  #options = [];

  connectedCallback() {
    this.#select = this.querySelector('[data-frame-mode-select]');
    this.#trigger = this.querySelector('[data-frame-mode-trigger]');
    this.#panel = this.querySelector('[data-frame-mode-panel]');
    this.#currentLabel = this.querySelector('[data-frame-mode-current-label]');
    this.#currentSwatch = this.querySelector('[data-frame-mode-current-swatch]');
    this.#options = Array.from(this.querySelectorAll('[data-frame-mode-option]'));

    if (!(this.#select instanceof HTMLSelectElement) || !(this.#trigger instanceof HTMLButtonElement) || !(this.#panel instanceof HTMLElement)) return;

    this.#storageKey = this.dataset.storageKey || `frame-mode:${window.location.pathname}`;
    this.#sectionId = this.dataset.sectionId || '';

    this.#select.addEventListener('change', this.#handleSelectChange);
    this.#trigger.addEventListener('click', this.#handleTriggerClick);
    this.#panel.addEventListener('click', this.#handlePanelClick);
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.addEventListener('keydown', this.#handleDocumentKeyDown);
    window.addEventListener('collection:frame-mode-change', this.#handleExternalFrameModeChange);

    const initialMode = this.#getInitialMode();
    this.#setMode(initialMode, false);
  }

  disconnectedCallback() {
    this.#select?.removeEventListener('change', this.#handleSelectChange);
    this.#trigger?.removeEventListener('click', this.#handleTriggerClick);
    this.#panel?.removeEventListener('click', this.#handlePanelClick);
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.removeEventListener('keydown', this.#handleDocumentKeyDown);
    window.removeEventListener('collection:frame-mode-change', this.#handleExternalFrameModeChange);
  }

  #handleSelectChange = () => {
    const mode = this.#normalizeMode(this.#select?.value || this.#getDefaultMode());
    this.#setMode(mode, true);
    this.#dispatchChange(mode);
  };

  #handleTriggerClick = () => {
    if (!(this.#panel instanceof HTMLElement) || !(this.#trigger instanceof HTMLButtonElement)) return;

    if (this.#panel.hidden) {
      this.#openPanel();
      return;
    }

    this.#closePanel();
  };

  #handlePanelClick = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[data-frame-mode-option]') : null;
    if (!(option instanceof HTMLButtonElement)) return;

    const mode = this.#normalizeMode(option.dataset.mode || this.#getDefaultMode());
    this.#setMode(mode, true);
    this.#dispatchChange(mode);
    this.#closePanel();
  };

  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#closePanel();
  };

  #handleDocumentKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.#closePanel();
  };

  #handleExternalFrameModeChange = (event) => {
    const detail = event?.detail;
    if (!detail || detail.storageKey !== this.#storageKey) return;
    this.#setMode(this.#normalizeMode(detail.mode), false);
  };

  #dispatchChange(mode) {
    window.dispatchEvent(
      new CustomEvent('collection:frame-mode-change', {
        detail: {
          mode,
          storageKey: this.#storageKey,
        },
      })
    );
  }

  #getInitialMode() {
    const fromSession = sessionStorage.getItem(this.#storageKey);
    if (fromSession) return this.#normalizeMode(fromSession);

    const root = this.#getResultsList();
    const fromDom = root?.dataset.frameMode;
    if (fromDom) return this.#normalizeMode(fromDom);

    return this.#normalizeMode(this.#select?.value || this.#getDefaultMode());
  }

  #getDefaultMode() {
    if (!(this.#select instanceof HTMLSelectElement)) return 'featured';

    const defaultOption = Array.from(this.#select.options).find((option) => option.defaultSelected);
    return defaultOption?.value || this.#select.options[0]?.value || 'featured';
  }

  #normalizeMode(mode) {
    const defaultMode = this.#getDefaultMode();
    if (!(this.#select instanceof HTMLSelectElement)) return defaultMode;

    const validValues = Array.from(this.#select.options).map((option) => option.value);
    if (validValues.includes(mode)) return mode;
    if (validValues.includes(defaultMode)) return defaultMode;
    return validValues[0] || defaultMode;
  }

  #getResultsList() {
    if (this.#sectionId) {
      const bySection = document.querySelector(`results-list[section-id="${this.#sectionId}"]`);
      if (bySection instanceof HTMLElement) return bySection;
    }

    const byClosest = this.closest('results-list');
    if (byClosest instanceof HTMLElement) return byClosest;

    const fallback = document.querySelector('results-list');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  #setMode(mode, persist = true) {
    if (this.#select && this.#select.value !== mode) {
      this.#select.value = mode;
    }

    const activeOption = this.#options.find((option) => option.dataset.mode === mode) || this.#options[0] || null;

    this.#options.forEach((option) => {
      const isActive = option === activeOption;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.#syncTrigger(activeOption);

    const root = this.#getResultsList();
    if (root) {
      root.dataset.frameMode = mode;
    }

    if (persist) {
      sessionStorage.setItem(this.#storageKey, mode);
    }
  }

  #syncTrigger(activeOption) {
    if (!(activeOption instanceof HTMLButtonElement)) return;

    const label = activeOption.dataset.label || activeOption.textContent?.trim() || 'Featured';
    const key = (activeOption.dataset.key || 'featured').toLowerCase().replace(/[^a-z0-9_-]/g, '');

    if (this.#currentLabel) {
      this.#currentLabel.textContent = label;
    }

    if (this.#currentSwatch) {
      this.#currentSwatch.className = `frame-mode-control__swatch frame-swatch frame-swatch--${key}`;
    }

    if (this.#trigger) {
      this.#trigger.setAttribute('aria-label', `Marco: ${label}`);
    }
  }

  #openPanel() {
    if (!(this.#panel instanceof HTMLElement) || !(this.#trigger instanceof HTMLButtonElement)) return;

    this.#panel.hidden = false;
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  #closePanel() {
    if (!(this.#panel instanceof HTMLElement) || !(this.#trigger instanceof HTMLButtonElement)) return;
    if (this.#panel.hidden) return;

    this.#panel.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }
}

if (!customElements.get('frame-mode-control')) {
  customElements.define('frame-mode-control', FrameModeControl);
}
